/**
 * MesObjectifs — objectifs hebdomadaires et mensuels du commercial connecté (et de l'équipe pour l'administrateur),
 * réalisé calculé depuis le suivi de prospection. Source : GET /api/v2/objectifs/suivi
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Paper, Chip, Button, CircularProgress, Alert, Grid, LinearProgress, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';
const auth = () => { const t = localStorage.getItem('token'); return t ? { Authorization: `Bearer ${t}` } : {}; };

function Jauge({ label, fait, objectif, couleur = 'primary' }) {
  const pct = objectif ? Math.min(100, Math.round(100 * fait / objectif)) : null;
  return (
    <Box sx={{ mb: 1.2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="body2" fontWeight={600}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 800, color: pct == null ? '#666' : pct >= 100 ? '#2e7d32' : pct >= 60 ? '#e65100' : '#c62828' }}>
          {fait}{objectif ? ` / ${objectif}` : ''}{pct != null ? ` (${pct} %)` : ''}
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct ?? 0} color={pct == null ? 'inherit' : pct >= 100 ? 'success' : pct >= 60 ? 'warning' : 'error'} sx={{ height: 9, borderRadius: 5, bgcolor: '#eee' }} />
    </Box>
  );
}

function Barres({ points, cle, titre }) {
  const max = Math.max(1, ...points.map(p => p[cle] || 0));
  return (
    <Box>
      <Typography variant="caption" fontWeight={700} color="text.secondary">{titre}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.6, height: 70, mt: 0.5 }}>
        {points.map((p, i) => (
          <Tooltip key={i} title={`${new Date(p.debut).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} : ${p[cle] || 0}`}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>{p[cle] || ''}</Typography>
              <Box sx={{ width: '100%', height: `${Math.max(4, 60 * (p[cle] || 0) / max)}px`, bgcolor: i === points.length - 1 ? '#1565c0' : '#90caf9', borderRadius: '3px 3px 0 0' }} />
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}

export function CarteCommercial({ c, moi }) {
  const zones = c.zones || [];
  const cps = [...new Set(zones.map(z => z.code_postal).filter(Boolean))];
  const couv = c.couverture || {};
  const pctCouv = couv.decile_1 ? Math.round(100 * couv.decile_1_suivies / couv.decile_1) : 0;
  return (
    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, borderTop: `4px solid ${c.couleur || '#1565c0'}`, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight={800}>{c.nom || c.email}{moi ? ' (moi)' : ''}</Typography>
        {c.enregistre === false && <Chip size="small" label="Objectifs non définis" color="warning" variant="outlined" />}
        {!c.actif && <Chip size="small" label="Inactif" />}
      </Box>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">Zones</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
          {zones.length === 0 && <Typography variant="body2" color="text.disabled">Aucune zone attribuée</Typography>}
          {zones.slice(0, 12).map(z => <Chip key={z.code_commune_insee} size="small" label={`${z.commune || z.code_commune_insee}${z.code_postal ? ' ' + z.code_postal : ''}`} sx={{ height: 22 }} />)}
          {zones.length > 12 && <Chip size="small" label={`+${zones.length - 12}`} sx={{ height: 22 }} />}
        </Box>
        {couv.decile_1 > 0 && (
          <Typography variant="body2" sx={{ mt: 0.8 }}>
            Décile 1 dans ces zones : <b>{couv.decile_1.toLocaleString('fr-FR')}</b> maisons, dont <b>{couv.decile_1_suivies}</b> déjà suivies ({pctCouv} %).
            {cps.map(cp => <Button key={cp} size="small" href={`/search?cp=${cp}`} sx={{ textTransform: 'none', ml: 0.5, py: 0 }}>Liste {cp}</Button>)}
          </Typography>
        )}
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={800} gutterBottom>Cette semaine <span style={{ color: '#888', fontWeight: 400 }}>({new Date(c.semaine.debut).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} au {new Date(c.semaine.fin).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })})</span></Typography>
          <Jauge label="Portes frappées" fait={c.semaine.portes} objectif={c.obj_portes_hebdo} />
          <Jauge label="Fiches remplies" fait={c.semaine.fiches} objectif={c.obj_fiches_hebdo} />
          <Jauge label="Rendez-vous" fait={c.semaine.rdv} objectif={c.obj_rdv_hebdo} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={800} gutterBottom>Ce mois <span style={{ color: '#888', fontWeight: 400 }}>({new Date(c.mois.debut).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })})</span></Typography>
          <Jauge label="Portes frappées" fait={c.mois.portes} objectif={c.obj_portes_mois} />
          <Jauge label="Rendez-vous" fait={c.mois.rdv} objectif={c.obj_rdv_mois} />
          <Jauge label="Mandats signés" fait={c.mois.mandats} objectif={c.obj_mandats_mois} />
          <Typography variant="caption" color="text.secondary">Projets de vente déclarés ce mois : {c.mois.projets}</Typography>
        </Grid>
        <Grid item xs={12} md={6}><Barres points={c.historique_semaines} cle="portes" titre="Portes par semaine (8 dernières)" /></Grid>
        <Grid item xs={12} md={6}><Barres points={c.historique_mois} cle="rdv" titre="Rendez-vous par mois (6 derniers)" /></Grid>
      </Grid>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
        {c.total_fiches} fiche{c.total_fiches > 1 ? 's' : ''} au total{c.depuis ? ` depuis le ${new Date(c.depuis).toLocaleDateString('fr-FR')}` : ''}. Réalisé calculé depuis le suivi de prospection (fiches à votre nom).
      </Typography>
    </Paper>
  );
}

export default function MesObjectifs({ user }) {
  const [moi, setMoi] = useState(null);
  const [equipe, setEquipe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r1 = await axios.get(`${API_BASE}/v2/objectifs/suivi?email=${encodeURIComponent(user.email)}`, { headers: auth(), timeout: 30000 });
      setMoi(r1.data.commerciaux[0]);
      if (isAdmin) {
        const r2 = await axios.get(`${API_BASE}/v2/objectifs/suivi`, { headers: auth(), timeout: 60000 });
        setEquipe(r2.data.commerciaux.filter(c => c.email !== user.email.toLowerCase()));
      }
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [user, isAdmin]);
  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight={800} gutterBottom>🏁 Mes objectifs</Typography>
          <Typography variant="body2" color="text.secondary">Semaine du lundi au dimanche et mois en cours. Une porte compte quand la fiche porte une date de prospection et un statut terrain.</Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading} sx={{ ml: 'auto' }}>Actualiser</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !moi && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
      {moi && (
        <>
          {moi.enregistre === false && <Alert severity="info" sx={{ mb: 2 }}>Vos zones et objectifs ne sont pas encore définis : demandez à l'administrateur de vous ajouter dans « Commerciaux et zones ». Votre réalisé est déjà compté.</Alert>}
          <CarteCommercial c={moi} moi />
        </>
      )}
      {isAdmin && equipe && equipe.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" fontWeight={800} gutterBottom>Équipe</Typography>
          <Grid container spacing={2}>
            {equipe.map(c => <Grid item xs={12} lg={6} key={c.email}><CarteCommercial c={c} /></Grid>)}
          </Grid>
        </Box>
      )}
    </Box>
  );
}
